import type React from "react";
import { Link } from "react-router-dom";
import {
  CarouselItemStatus,
  CarouselActionType,
  NavigationTarget,
  type ICarouselItem,
} from "../types/models/carousel.models.types";
import { ActiveBadge, ClosedBadge, UnderReviewBadge } from "./badges.components";
import { Button } from "./ui/button";
import ArrowTopRightIcon from "../assets/svgs/common/arrow-top-right.svg";
import AddImageIcon from "../assets/svgs/common/add-image.svg";
import { ExternalLink, Navigation } from "lucide-react";

interface Props {
  item: ICarouselItem;
  onPublish?: (id: string) => void;
  onArchive?: (id: string) => void;
  publishing?: boolean;
  archiving?: boolean;
}

const CarouselItemCard: React.FunctionComponent<Props> = ({
  item,
  onPublish,
  onArchive,
  publishing,
  archiving,
}) => {
  const isDraft = item.status.status === CarouselItemStatus.DRAFT;
  const isLive = item.status.status === CarouselItemStatus.LIVE;
  const isArchived = item.status.status === CarouselItemStatus.ARCHIVED;

  const createdAt = new Date(item.createdAt).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const actionLabel =
    item.action.type === CarouselActionType.LINK
      ? item.action.url ?? "—"
      : item.action.navigation?.target === NavigationTarget.CAMPAIGN_SCREEN
        ? `Campaign`
        : item.action.navigation?.target === NavigationTarget.PAYOUTS_SCREEN
          ? "Payouts Screen"
          : item.action.navigation?.target === NavigationTarget.REFER_AND_EARN_SCREEN
            ? "Refer & Earn Screen"
            : "—";

  return (
    <div className="flex flex-col gap-2 pb-5 border border-border rounded-10 w-full overflow-hidden">
      {/* Image */}
      <div className="relative rounded-b-10 w-full overflow-hidden aspect-video">
        <div
          style={{
            background:
              "linear-gradient(180deg, #131313 0%, rgba(19, 19, 19, 0.64) 50.5%, rgba(19, 19, 19, 0.00) 100%)",
          }}
          className="absolute w-full h-20 z-10"
        />

        {/* Action type badge */}
        <span className="top-3 left-0 absolute z-20 flex items-center gap-1 bg-badgebg backdrop-blur-sm px-4 py-2 pl-6 border-border border-t border-r border-b rounded-r-full font-bold text-sm">
          {item.action.type === CarouselActionType.NAVIGATION ? (
            <><Navigation className="w-3 h-3" /> navigation</>
          ) : (
            <><ExternalLink className="w-3 h-3" /> link</>
          )}
        </span>

        {/* Created date */}
        <span className="top-3 right-3 absolute z-20 flex items-center gap-1 bg-badgebg backdrop-blur-sm px-3 py-2 border border-border rounded-full font-bold text-xs">
          {createdAt}
        </span>

        {item.asset?.url ? (
          <img
            src={item.asset.url}
            className="border-border border-b rounded-b-xl w-full h-full aspect-video object-cover"
            alt={item.asset.name}
          />
        ) : (
          <div className="top-0 left-0 absolute flex justify-center items-center bg-opacity-50 border-border border-b rounded-xl w-full h-full">
            <AddImageIcon />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4 px-4 pt-1">
        {/* Badges row */}
        <div className="flex items-center gap-2 flex-wrap">
          {isLive && <ActiveBadge />}
          {isDraft && <UnderReviewBadge />}
          {isArchived && <ClosedBadge />}
        </div>

        {/* Action info */}
        <div className="flex flex-col gap-0.5">
          <p className="text-secondary text-xs font-semibold uppercase tracking-wide">
            {item.action.type === CarouselActionType.LINK
              ? "Link target"
              : "Navigation target"}
          </p>
          <p
            className="text-sm font-semibold truncate w-full"
            title={actionLabel}
          >
            {actionLabel}
          </p>
        </div>

        {/* Creator info */}
        <div className="flex flex-col gap-0.5">
          <p className="text-secondary text-xs font-semibold uppercase tracking-wide">
            Created by
          </p>
          <p className="text-sm font-semibold truncate w-full">
            {item.status.user.name ||
              item.status.user.email ||
              item.status.user.id}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {isDraft && (
            <Link to={`/admin/carousels/${item.id}/edit`} className="w-full">
              <Button className="flex items-center gap-1 bg-tertiarybuttonbg px-9 py-6 border border-border rounded-lg w-full font-semibold text-base text-secondarybuttontext tracking-wider">
                Edit <ArrowTopRightIcon />
              </Button>
            </Link>
          )}

          {isDraft && onPublish && (
            <Button
              onClick={() => onPublish(item.id)}
              disabled={publishing}
              className="flex items-center justify-center gap-1 bg-primarybuttonbg px-9 py-6 border border-border rounded-lg w-full font-semibold text-base text-primarybuttontext tracking-wider"
            >
              Make live →
            </Button>
          )}

          {isLive && onArchive && (
            <Button
              onClick={() => onArchive(item.id)}
              disabled={archiving}
              className="flex items-center justify-center gap-1 bg-tertiarybuttonbg px-9 py-6 border border-dashed border-border rounded-lg w-full font-semibold text-base text-secondarybuttontext tracking-wider"
            >
              Archive
            </Button>
          )}

          {isArchived && (
            <div className="flex items-center justify-center py-4 text-secondary text-sm font-semibold">
              Archived
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarouselItemCard;
